import { createApi } from '@reduxjs/toolkit/query/react';

import {
  Supplier,
  SupplierListItem,
  AssessmentScore,
  AssessmentSubmission,
  AssessmentRecord,
  User,
  SupplierRegistrationAPIRequest,
  SupplierRegistration,
  SupportTicket,
  SupportTicketsResponse,
  UpdateSupportTicketProps,
  FeedbackSubmission,
  SupplierRegistrationCode,
  CreateRegistrationCodeRequest,
  UpdateRegistrationCodeRequest,
  PlatformMetrics,
  UserUpdateProps,
  ScaleupGrowthPlan,
  EnterpriseCustomer,
  CreateEnterpriseCustomerProps,
  UpdateEnterpriseCustomerProps,
  CreateFeedbackAPIProps,
  UserMetrics,
  SupplierMetrics,
  SupplierRegistrationMetrics,
  SupplierSubscription,
  UpdateSupplierAPIRequest,
  FirefliesShortSentence,
} from '@scaleup/models';
import { baseQuery } from './baseQueryAdmin';
import { TagTypes } from './tagTypes';
import { Tag } from '../ui';

export interface Suppliers {
  suppliers: Supplier[];
  exclusiveStartKey?: string;
}

export interface Users {
  users: User[];
  exclusiveStartKey?: string;
}

export interface FeedbackSubmissions {
  submissions: FeedbackSubmission[];
  exclusiveStartKey?: string;
}

export interface RegistrationCodesResponse {
  registrationCodes: SupplierRegistrationCode[];
}

export interface RegistrationsResponse {
  registrations: SupplierRegistration[];
  exclusiveStartKey?: string;
}

export interface PlatformMetricsResponse {
  metrics: PlatformMetrics[];
  count: number;
  startingDate: number;
}

export interface EnterpriseCustomers {
  customers: EnterpriseCustomer[];
  exclusiveStartKey?: string;
}

export interface GetUserSRASubmissionRequest {
  supplierId: string;
  userId: string;
  recordId: string;
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQuery,
  tagTypes: [
    TagTypes.assessments,
    TagTypes.assessment,
    TagTypes.assessmentSubmission,
    TagTypes.assessmentRecord,
    TagTypes.assessmentScore,
    TagTypes.users,
    TagTypes.suppliers,
    TagTypes.supplier,
    TagTypes.supportTickets,
    TagTypes.feedbackSubmissions,
    TagTypes.registrationCodes,
    TagTypes.registrations,
    TagTypes.platformMetrics,
    TagTypes.enterpriseCustomers,
  ],
  endpoints: (builder) => ({
    getSuppliersAdmin: builder.query<SupplierListItem[], void>({
      query: () => '/admin/suppliers?subscribed=true',
      providesTags: [TagTypes.suppliers],
    }),
    getSupplierAdmin: builder.query<Supplier, string>({
      query: (id: string) => `/admin/supplier/${id}`,
      providesTags: [TagTypes.supplier],
    }),
    getSupplierSRAScoreAdmin: builder.query<AssessmentScore, string>({
      query: (id: string) => `/admin/supplier/${id}/sra/score`,
    }),
    getSupplierSRARecordAdmin: builder.query<AssessmentRecord, string>({
      query: (id: string) => `/admin/supplier/${id}/sra/record`,
    }),
    getSupplierSRASubmissionAdmin: builder.query<AssessmentSubmission, string>({
      query: (id: string) => `/admin/supplier/${id}/sra`,
      providesTags: (result, error, arg) =>
        result
          ? [{ type: TagTypes.assessmentSubmission, id: result.submissionId }]
          : [TagTypes.assessmentSubmission]
    }),
    getSupplierSRAUserSubmissionAdmin: builder.query<AssessmentSubmission, GetUserSRASubmissionRequest>({
      query: ({ supplierId, userId, recordId }) => `/admin/supplier/${supplierId}/sra/submission?user_id=${userId}&record_id=${recordId}`,
      providesTags: (result, error, arg) =>
        result
          ? [{ type: TagTypes.assessmentSubmission, id: result.submissionId }]
          : [TagTypes.assessmentSubmission],
    }),
    getSupplierSGPAdmin: builder.query<ScaleupGrowthPlan, string>({
      query: (id: string) => `/admin/supplier/${id}/sgp`,
    }),
    getSupplierSGPDocsAdmin: builder.query<{url: string}, { supplierId: string; key: string }>({
      query: ({ supplierId, key }) => ({
        url: `/admin/supplier/${supplierId}/sgp/doc/${key}`,
      }),
    }),
    getSupplierSGPTranscriptAdmin: builder.query<FirefliesShortSentence[], { supplierId: string; key: string }>({
      query: ({ supplierId, key }) => ({
        url: `/admin/supplier/${supplierId}/sgp/transcript/${key}`,
      }),
    }),
    getSupplierReportByTypeAdmin: builder.query<{url: string}, { supplierId: string; reportType: string }>({
      query: ({ supplierId, reportType }) => ({
        url: `/admin/supplier/${supplierId}/report/${reportType}`,
      }),
    }),
    getUsersAdmin: builder.query<Users, string>({
      query: (exclusiveStartKey?: string) =>
        `/admin/users` +
        (exclusiveStartKey ? `?exclusiveStartKey=${exclusiveStartKey}` : ''),
      providesTags: [TagTypes.users],
    }),
    getUserAdmin: builder.query<User, string>({
      query: (id: string) => `/admin/user/${id}`,
    }),
    createSupplierAdmin: builder.mutation<
      SupplierRegistration,
      SupplierRegistrationAPIRequest
    >({
      query: (request: SupplierRegistrationAPIRequest) => ({
        url: `admin/supplier`,
        method: 'POST',
        body: request,
      }),
    }),
    createUserAdmin: builder.mutation<User, any>({
      query: (request: any) => ({
        url: `admin/user`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [TagTypes.users],
    }),
    updateUserAdmin: builder.mutation<User, {id: string, updates: UserUpdateProps}>({
      query: ({ id, updates }) => ({
        url: `admin/user/${id}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: [TagTypes.users],
    }),
    updateSupplierSubscriptionAdmin: builder.mutation<SupplierSubscription, { supplierId: string; subscription: SupplierSubscription }>({
      query: ({ supplierId, subscription }) => ({
        url: `/admin/supplier/${supplierId}/subscription`,
        method: 'PUT',
        body: subscription,
      }),
      invalidatesTags: [TagTypes.supplier],
    }),
    updateSupplierByIdAdmin: builder.mutation<Supplier, { supplierId: string; updates: UpdateSupplierAPIRequest }>({
      query: ({ supplierId, updates }) => ({
        url: `/admin/supplier/${supplierId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: [TagTypes.supplier],
    }),

    // Support Tickets Admin Endpoints
    getSupportTicketsAdmin: builder.query<SupportTicketsResponse, string | undefined>({
      query: (exclusiveStartKey?: string) =>
        '/admin/support/tickets' +
        (exclusiveStartKey ? `?exclusiveStartKey=${exclusiveStartKey}` : ''),
      providesTags: [TagTypes.supportTickets],
    }),
    getSupportTicketAdmin: builder.query<SupportTicket, string>({
      query: (ticketId: string) => `/admin/support/ticket/${ticketId}`,
      providesTags: [TagTypes.supportTickets],
    }),
    updateSupportTicketAdmin: builder.mutation<SupportTicket, UpdateSupportTicketProps>({
      query: ({ ticketId, ...updates }) => ({
        url: `/admin/support/ticket/${ticketId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: [TagTypes.supportTickets],
    }),

    // Feedback Submissions Admin Endpoints
    getFeedbackSubmissionsAdmin: builder.query<FeedbackSubmissions, string | undefined>({
      query: (exclusiveStartKey?: string) =>
        '/admin/support/feedback' +
        (exclusiveStartKey ? `?exclusiveStartKey=${exclusiveStartKey}` : ''),
      providesTags: [TagTypes.feedbackSubmissions],
    }),
    getFeedbackSubmissionAdmin: builder.query<FeedbackSubmission, string>({
      query: (submissionId: string) => `/admin/support/feedback/${submissionId}`,
      providesTags: [TagTypes.feedbackSubmissions],
    }),

    // Registrations Admin Endpoints
    getRegistrationsAdmin: builder.query<RegistrationsResponse, string | undefined>({
      query: (exclusiveStartKey?: string) =>
        '/admin/registrations' +
        (exclusiveStartKey ? `?exclusiveStartKey=${exclusiveStartKey}` : ''),
      providesTags: [TagTypes.registrations],
    }),

    // Registration Codes Admin Endpoints
    getRegistrationCodesAdmin: builder.query<RegistrationCodesResponse, void>({
      query: () => '/admin/code/registration',
      providesTags: [TagTypes.registrationCodes],
    }),
    getRegistrationCodeAdmin: builder.query<SupplierRegistrationCode, string>({
      query: (code: string) => `/admin/code/registration/${code}`,
      providesTags: [TagTypes.registrationCodes],
    }),
    createRegistrationCodeAdmin: builder.mutation<SupplierRegistrationCode, CreateRegistrationCodeRequest>({
      query: (request: CreateRegistrationCodeRequest) => ({
        url: '/admin/code/registration',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [TagTypes.registrationCodes],
    }),
    updateRegistrationCodeAdmin: builder.mutation<SupplierRegistrationCode, { code: string; updates: UpdateRegistrationCodeRequest }>({
      query: ({ code, updates }) => ({
        url: `/admin/code/registration/${code}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: [TagTypes.registrationCodes],
    }),

    // Platform Metrics Admin Endpoints
    getPlatformMetricsAdmin: builder.query<PlatformMetricsResponse, number | undefined>({
      query: (startingDate?: number) =>
        '/admin/metrics' +
        (startingDate ? `?starting_date=${startingDate}` : ''),
      providesTags: [TagTypes.platformMetrics],
    }),
    getUserMetricsAdmin: builder.query<UserMetrics, void>({
      query: () => '/admin/metrics/users',
      providesTags: [TagTypes.platformMetrics],
    }),
    getSupplierMetricsAdmin: builder.query<SupplierMetrics, void>({
      query: () => '/admin/metrics/suppliers',
      providesTags: [TagTypes.platformMetrics],
    }),
    getRegistrationMetricsAdmin: builder.query<SupplierRegistrationMetrics, void>({
      query: () => '/admin/metrics/registrations',
      providesTags: [TagTypes.platformMetrics],
    }),

    // Enterprise Customers Admin Endpoints
    getEnterpriseCustomersAdmin: builder.query<EnterpriseCustomers, string | undefined>({
      query: (exclusiveStartKey?: string) =>
        '/admin/enterprise_customers' +
        (exclusiveStartKey ? `?exclusiveStartKey=${exclusiveStartKey}` : ''),
      providesTags: [TagTypes.enterpriseCustomers],
    }),
    getEnterpriseCustomerAdmin: builder.query<EnterpriseCustomer, string>({
      query: (customerId: string) => `/admin/enterprise_customer/${customerId}`,
      providesTags: [TagTypes.enterpriseCustomers],
    }),
    createEnterpriseCustomerAdmin: builder.mutation<EnterpriseCustomer, CreateEnterpriseCustomerProps>({
      query: (request: CreateEnterpriseCustomerProps) => ({
        url: '/admin/enterprise_customer',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [TagTypes.enterpriseCustomers],
    }),
    updateEnterpriseCustomerAdmin: builder.mutation<EnterpriseCustomer, { customerId: string; updates: UpdateEnterpriseCustomerProps }>({
      query: ({ customerId, updates }) => ({
        url: `/admin/enterprise_customer/${customerId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: [TagTypes.enterpriseCustomers],
    }),

    // Enterprise Customer Feedback Admin Endpoints
    createEnterpriseCustomerFeedbackAdmin: builder.mutation<EnterpriseCustomer, { customerId: string; feedback: CreateFeedbackAPIProps }>({
      query: ({ customerId, feedback }) => ({
        url: `/admin/enterprise_customer/${customerId}/feedback`,
        method: 'POST',
        body: feedback,
      }),
      invalidatesTags: [TagTypes.enterpriseCustomers],
    }),
    updateEnterpriseCustomerFeedbackAdmin: builder.mutation<EnterpriseCustomer, { customerId: string; feedbackId: string; feedback: CreateFeedbackAPIProps }>({
      query: ({ customerId, feedbackId, feedback }) => ({
        url: `/admin/enterprise_customer/${customerId}/feedback/${feedbackId}`,
        method: 'PUT',
        body: feedback,
      }),
      invalidatesTags: [TagTypes.enterpriseCustomers],
    }),
  }),
});

export const {
  useGetSuppliersAdminQuery,
  useGetSupplierAdminQuery,
  useGetSupplierSRAScoreAdminQuery,
  useGetSupplierSRASubmissionAdminQuery,
  useGetSupplierSRARecordAdminQuery,
  useGetSupplierSRAUserSubmissionAdminQuery,
  useGetSupplierSGPAdminQuery,
  useGetSupplierSGPDocsAdminQuery,
  useLazyGetSupplierSGPDocsAdminQuery,
  useGetSupplierSGPTranscriptAdminQuery,
  useLazyGetSupplierSGPTranscriptAdminQuery,
  useGetSupplierReportByTypeAdminQuery,
  useLazyGetSupplierReportByTypeAdminQuery,
  useGetUsersAdminQuery,
  useGetUserAdminQuery,
  useCreateSupplierAdminMutation,
  useCreateUserAdminMutation,
  useUpdateUserAdminMutation,
  useUpdateSupplierSubscriptionAdminMutation,
  useUpdateSupplierByIdAdminMutation,
  useGetSupportTicketsAdminQuery,
  useGetSupportTicketAdminQuery,
  useUpdateSupportTicketAdminMutation,
  useGetFeedbackSubmissionsAdminQuery,
  useGetFeedbackSubmissionAdminQuery,
  useGetRegistrationsAdminQuery,
  useGetRegistrationCodesAdminQuery,
  useGetRegistrationCodeAdminQuery,
  useCreateRegistrationCodeAdminMutation,
  useUpdateRegistrationCodeAdminMutation,
  useGetPlatformMetricsAdminQuery,
  useGetUserMetricsAdminQuery,
  useGetSupplierMetricsAdminQuery,
  useGetRegistrationMetricsAdminQuery,
  useGetEnterpriseCustomersAdminQuery,
  useGetEnterpriseCustomerAdminQuery,
  useCreateEnterpriseCustomerAdminMutation,
  useUpdateEnterpriseCustomerAdminMutation,
  useCreateEnterpriseCustomerFeedbackAdminMutation,
  useUpdateEnterpriseCustomerFeedbackAdminMutation,
} = adminApi;
